/**
 * Calls API contracts: call ringing (`/v1/calls`).
 *
 * Stream Video owns the call itself (rooms, media, accept/reject state). These
 * endpoints only move the "ring the phone" push from Stream's per-app push
 * providers to our own:
 *
 *  - iOS rings through a PushKit VoIP push the backend sends straight to APNs,
 *    signed with the app's `.p8` key, so the device registers its PushKit token
 *    with the backend (`PUT /v1/calls/voip-devices`).
 *  - Android rings through a high-priority data-only push sent by the school's
 *    existing OneSignal app; its FCM registration already exists.
 *
 * The caller's app asks the backend to ring (`POST /v1/calls/:callCid/ring`)
 * right after `call.getOrCreate({ ring: true })`. Any participant's app asks the
 * backend to stop ringing (`POST /v1/calls/:callCid/ring/cancel`); the backend
 * reads the call from Stream and decides who must stop, so a client cannot
 * cancel a ring Stream still considers live.
 *
 * Every school app rings this way, with no flag in front of it (SCH-2496); an
 * older binary built for in-app calls calls none of it.
 */

import { z } from 'zod'

/** The payload key every call push carries. Its value is the action. */
export const CALL_RING_PUSH_KEY = 'sj_call' as const

export const CallRingActionSchema = z.enum(['ring', 'cancel'])
export type CallRingAction = z.infer<typeof CallRingActionSchema>

export const CallRingCancelReasonSchema = z.enum([
  // The caller hung up, or the call ended, before this callee answered.
  'caller_cancelled',
  // This callee answered on another device.
  'answered_elsewhere',
  // This callee declined on another device.
  'declined_elsewhere',
  // Nobody answered inside the ring window.
  'timeout'
])
export type CallRingCancelReason = z.infer<typeof CallRingCancelReasonSchema>

/**
 * The `data` object of a OneSignal call push, as the device receives it. Every
 * value is a string: Android flattens FCM data to strings, and the native
 * handlers on both platforms read it without a JSON schema library.
 *
 * iOS receives it at `custom.a` of the PushKit payload; Android receives it at
 * `custom.a` of the FCM data map (`custom` is a JSON string there).
 */
export const CallRingPushDataSchema = z.object({
  [CALL_RING_PUSH_KEY]: CallRingActionSchema,
  /** Stream call cid, `type:id` (e.g. `default:abc`). */
  call_cid: z.string().min(1),
  /** Stream user id of the callee this push is for (UsersOnSchools.publicId). */
  receiver_id: z.string().min(1),
  /** Stream user id of the caller (UsersOnSchools.publicId). */
  created_by_id: z.string().min(1),
  created_by_display_name: z.string(),
  /** `'true'` for a video call, `'false'` for voice. */
  video: z.enum(['true', 'false']),
  /** Epoch milliseconds after which a ring must not start ringing. */
  expires_at: z.string().regex(/^\d+$/),
  /** Present on `cancel` only. */
  cancel_reason: CallRingCancelReasonSchema.optional(),
  /**
   * Present on `cancel` only: the install that asked for the cancel. That
   * install ignores its own cancel so an answer never tears down its own call.
   */
  origin_install_id: z.string().optional()
})
export type CallRingPushData = z.infer<typeof CallRingPushDataSchema>

/** Path params for `/v1/calls/:callCid/...`. The cid is URL-encoded by the client. */
export const CallCidParamsSchema = z.object({
  callCid: z
    .string()
    .min(3)
    .max(200)
    .regex(/^[a-z0-9_-]+:[A-Za-z0-9_-]+$/, 'callCid must be a Stream cid (type:id)')
})
export type CallCidParams = z.infer<typeof CallCidParamsSchema>

/** POST /v1/calls/:callCid/ring — no body: every input comes from Stream. */
export const RingCallResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    /** Callees the backend attempted to ring (excludes the caller). */
    calleeCount: z.number().int().nonnegative(),
    /** Pushes accepted: OneSignal (Android, one per callee per app) plus APNs (one per registered iPhone). */
    sentCount: z.number().int().nonnegative(),
    failedCount: z.number().int().nonnegative()
  })
})
export type RingCallResponse = z.infer<typeof RingCallResponseSchema>

/** POST /v1/calls/:callCid/ring/cancel */
export const CancelCallRingRequestSchema = z.object({
  /** The asking install, so it ignores its own cancel. See `origin_install_id`. */
  originInstallId: z.string().min(8).max(64)
})
export type CancelCallRingRequest = z.infer<typeof CancelCallRingRequestSchema>

export const CancelCallRingResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    /** Callees whose devices were told to stop ringing, with the reason each got. */
    cancelled: z.array(z.object({ receiverId: z.string(), reason: CallRingCancelReasonSchema })),
    sentCount: z.number().int().nonnegative(),
    failedCount: z.number().int().nonnegative()
  })
})
export type CancelCallRingResponse = z.infer<typeof CancelCallRingResponseSchema>

export const VoipPushEnvironmentSchema = z.enum(['sandbox', 'production'])
export type VoipPushEnvironment = z.infer<typeof VoipPushEnvironmentSchema>

/**
 * PUT /v1/calls/voip-devices — register this iPhone's PushKit token in the
 * backend's VoIP device table, against the binary it runs (resolved from
 * `bundleId`) and owned by the caller's school-scoped identity. Idempotent: the
 * same token moves to the new owner (with a new `subscriptionId`) when the
 * active school or user changes.
 */
export const RegisterVoipDeviceRequestSchema = z.object({
  /** Hex PushKit token. Never log it. */
  token: z.string().regex(/^[0-9a-fA-F]{64,200}$/),
  /** Debug / TestFlight-less dev builds use APNs sandbox; store builds use production. Only production is rung. */
  environment: VoipPushEnvironmentSchema,
  /** iOS bundle id of the running binary: picks the APNs topic and signing key. */
  bundleId: z.string().min(3).max(200)
})
export type RegisterVoipDeviceRequest = z.infer<typeof RegisterVoipDeviceRequestSchema>

export const RegisterVoipDeviceResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    /**
     * The registration's id, kept by the device to unregister later. Named for the
     * OneSignal VoIP app it replaced; installed binaries parse this shape.
     */
    subscriptionId: z.string(),
    /** The binary's `MobileApp` public id. Named for the OneSignal VoIP app it replaced. */
    oneSignalVoipAppId: z.string()
  })
})
export type RegisterVoipDeviceResponse = z.infer<typeof RegisterVoipDeviceResponseSchema>

/**
 * DELETE /v1/calls/voip-devices — on logout, school switch away, or flag off.
 * Removes only the caller's own registration; `removed: false` otherwise.
 */
export const UnregisterVoipDeviceRequestSchema = z.object({
  subscriptionId: z.string().min(1),
  bundleId: z.string().min(3).max(200)
})
export type UnregisterVoipDeviceRequest = z.infer<typeof UnregisterVoipDeviceRequestSchema>

export const UnregisterVoipDeviceResponseSchema = z.object({
  message: z.string(),
  data: z.object({ removed: z.boolean() })
})
export type UnregisterVoipDeviceResponse = z.infer<typeof UnregisterVoipDeviceResponseSchema>

// Advanced Communications v1 (SCH-613/614/619/620/621).
export * from './call-audit'
export * from './create-call'
export * from './scheduled-sessions'
